import type { HttpClient } from '../http/types';
import type {
  BackendDepartment,
  BackendOrganizationalUnit,
  BackendOrganizationalUnitType,
  BackendPosition,
  BackendSpace,
  CreateOrganizationalUnitPayload,
} from '../types/organization-structure';
import type { AssignUnitLeaderPayload, BackendUnitLeader } from '../types/leadership';
import { ENDPOINTS } from './endpoints';

export function createOrganizationStructureApi(http: HttpClient) {
  return {
    positions: {
      list: () => http.get<BackendPosition[]>(ENDPOINTS.organizationStructure.positions),

      create: (name: string) =>
        http.post<BackendPosition>(ENDPOINTS.organizationStructure.positions, { name }),

      rename: (id: string, name: string) =>
        http.patch<BackendPosition>(ENDPOINTS.organizationStructure.positionById(id), {
          name,
        }),

      deactivate: (id: string) =>
        http.post<BackendPosition>(ENDPOINTS.organizationStructure.positionDeactivate(id)),
    },

    departments: {
      list: () => http.get<BackendDepartment[]>(ENDPOINTS.organizationStructure.departments),

      create: (spaceId: string, name: string) =>
        http.post<BackendDepartment>(ENDPOINTS.organizationStructure.departments, {
          spaceId,
          name,
        }),

      rename: (id: string, name: string) =>
        http.patch<BackendDepartment>(ENDPOINTS.organizationStructure.departmentById(id), {
          name,
        }),

      deactivate: (id: string) =>
        http.post<BackendDepartment>(ENDPOINTS.organizationStructure.departmentDeactivate(id)),
    },

    spaces: {
      list: () => http.get<BackendSpace[]>(ENDPOINTS.spaces.root),

      create: (name: string) => http.post<BackendSpace>(ENDPOINTS.spaces.root, { name }),

      update: (id: string, name: string) =>
        http.patch<BackendSpace>(ENDPOINTS.spaces.byId(id), { name }),

      deactivate: (id: string) => http.post<BackendSpace>(ENDPOINTS.spaces.deactivate(id)),
    },

    unitTypes: {
      list: () => http.get<BackendOrganizationalUnitType[]>(ENDPOINTS.organizationStructure.unitTypes),
    },

    units: {
      list: (filters: { typeId?: string } = {}) =>
        http.get<BackendOrganizationalUnit[]>(ENDPOINTS.organizationStructure.units, {
          // O contrato do BFF limita esta listagem a 50 itens.
          query: { limit: 50, ...filters },
        }),

      create: (payload: CreateOrganizationalUnitPayload) =>
        http.post<BackendOrganizationalUnit>(ENDPOINTS.organizationStructure.units, payload),

      rename: (id: string, name: string) =>
        http.patch<BackendOrganizationalUnit>(ENDPOINTS.organizationStructure.unitById(id), { name }),

      deactivate: (id: string) =>
        http.post<BackendOrganizationalUnit>(ENDPOINTS.organizationStructure.unitDeactivate(id)),
    },

    /** Nomeações de liderança de uma unidade organizacional ("Setor de liderança" no Figma). */
    unitLeaders: {
      list: (unitId: string) =>
        http.get<BackendUnitLeader[]>(ENDPOINTS.organizationalUnitLeaders.root(unitId)),

      assign: (unitId: string, payload: AssignUnitLeaderPayload) =>
        http.post<{ organizationalUnitLeaderId: string }>(
          ENDPOINTS.organizationalUnitLeaders.root(unitId),
          payload,
        ),

      end: (unitId: string, leaderId: string) =>
        http.delete<void>(ENDPOINTS.organizationalUnitLeaders.byId(unitId, leaderId)),
    },
  };
}

export type OrganizationStructureApi = ReturnType<typeof createOrganizationStructureApi>;
